// Utils
import GenericContact from '@/types/generic-contact';
import { BMO_CONTACT_TYPE } from './bmo-gif/constants';
import { userHasPermission, PERMISSIONS } from './permissions';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import ComparisonContact from '@/types/comparison-contact';

export enum CONTACT_LAYOUT {
  CONTACT = 'Contact',
  CLIENT = 'Client',
}

export enum CONTACT_CATEGORIES {
  CLIENT = 'CLIENT',
}

export const splitFullName = (fullName: string) => {
  const names = fullName.split(' ');
  return {
    fname: names[0] ?? '',
    lname: names.slice(1).join(' ') ?? '',
  };
};

export const getGenericContactNameFormat = (contact: ComparisonContact) => {
  if ('fullName' in contact && !contact.fname && !contact.lname) {
    const splittedName = contact.fullName ? splitFullName(contact.fullName) : { fname: '', lname: '' };
    contact = {
      ...contact,
      ...splittedName,
    };
  }
  return contact;
};

export const getContactInitials = (contact: GenericContact | ComparisonContact) => {
  contact = getGenericContactNameFormat(contact);
  let initials = '';
  if (contact.fname && contact.lname) {
    initials = `${contact.fname[0]}${contact.lname[0]}`;
  } else if (contact.fname || contact.lname) {
    const firstName = contact.fname != null ? contact.fname : '';
    const lastName = contact.lname != null ? contact.lname : '';
    const name = `${firstName} ${lastName}`.trim();
    if (name) {
      initials = name[0];
    }
  } else if (contact.email) {
    initials = contact.email[0];
  }

  return initials.toUpperCase();
};

export const getContactName = (contact: GenericContact | ComparisonContact) => {
  if (contact.fname || contact.lname) {
    const firstName = contact.fname != null ? contact.fname : '';
    const lastName = contact.lname != null ? contact.lname : '';
    return `${firstName} ${lastName}`.trim();
  }

  if ('fullName' in contact) {
    return contact.fullName;
  }

  if (contact.email) {
    return contact.email;
  }

  return '';
};

export const createComparisonContact = (contact: GenericContact) => {
  let comparisonContact = {
    _id: contact._id,
    fullName: getContactName(contact),
    locale: contact.locale,
    email: contact.email,
  };

  if (contact.type === BMO_CONTACT_TYPE) {
    comparisonContact = Object.assign(comparisonContact, {
      type: contact.type,
      dob: contact.dob,
      ownerIsAnnuitant: contact.ownerIsAnnuitant,
      fullNamePolicyOwner: contact.fnamePolicyOwner && contact.lnamePolicyOwner ? `${contact.fnamePolicyOwner} ${contact.lnamePolicyOwner}` : undefined,
      fullNameSecondPolicyOwner:
        contact.fnameSecondPolicyOwner && contact.lnameSecondPolicyOwner ? `${contact.fnameSecondPolicyOwner} ${contact.lnameSecondPolicyOwner}` : undefined,
      accountType: contact.accountType,
      province: contact.province,
      registeredAccountType: contact.registeredAccountType,
    });
  }

  return comparisonContact;
};

/**
 * Returns a contact layout.
 * Note: Currently used for changing verbiage between 'Contact' or 'Client'
 * @param {Nullable<User>} user User
 * @returns {string} Contact Layout
 */
export const getContactLayout = (user?: Nullable<User>) => {
  if (user && userHasPermission(user, PERMISSIONS.BMO_GIF)) {
    return CONTACT_LAYOUT.CLIENT;
  }

  return CONTACT_LAYOUT.CONTACT;
};
